import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import {
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Badge,
  Skeleton,
  Animate,
  EmptyState,
  Select,
  useToast
} from 'wss3-forge'
import {
  Receipt20Regular,
  Calendar20Regular
} from '@fluentui/react-icons'

interface Subscription {
  id: string
  plan: string
  status: string
  current_period_start?: string
  current_period_end?: string
  product_name?: string
  price?: number
  currency?: string
  interval?: string
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'past_due', label: 'Past due' }
]

export function Subscriptions() {
  const { user } = useAuth()
  const toast = useToast()
  usePageTitle('Subscriptions')
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const initialLoadDone = useRef(false)

  useEffect(() => {
    if (user && !initialLoadDone.current) {
      loadSubscriptions()
    }
  }, [user])

  const loadSubscriptions = async () => {
    if (!user) return

    setLoading(true)
    // TODO: Implement subscriptions table query when Stripe is set up
    setSubscriptions([])
    initialLoadDone.current = true
    setLoading(false)
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'canceled':
        return 'error'
      case 'past_due':
        return 'warning'
      case 'trialing':
        return 'primary'
      default:
        return 'default'
    }
  }

  const formatPrice = (price?: number, currency?: string, interval?: string) => {
    if (!price || !currency) return null
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price / 100)
    return interval ? `${formatted}/${interval}` : formatted
  }

  const filteredSubscriptions = subscriptions.filter(sub =>
    statusFilter === 'all' || sub.status === statusFilter
  )

  const handleManageSubscription = (sub: Subscription) => {
    // In production, this would redirect to Stripe Customer Portal
    toast.info(`Managing ${sub.product_name || sub.plan} subscription`)
  }

  return (
    <div className="page-content">
      <VStack gap="md">
        <Animate type="fadeIn">
          <HStack style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <VStack gap="xs">
              <Heading level={2}>Subscriptions</Heading>
              <Text color="muted">Manage your active subscriptions</Text>
            </VStack>
            <div style={{ width: '250px' }}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
              />
            </div>
          </HStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          {loading ? (
            <VStack gap="sm">
              <Card padding="md">
                <HStack gap="md" style={{ alignItems: 'center' }}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <VStack gap="xs" style={{ flex: 1 }}>
                    <Skeleton width={150} height={20} />
                    <Skeleton width={200} height={16} />
                  </VStack>
                </HStack>
              </Card>
            </VStack>
          ) : filteredSubscriptions.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                icon={<Receipt20Regular />}
                title={statusFilter === 'all' ? "No subscriptions" : "No matching subscriptions"}
                description={statusFilter === 'all' ? "You don't have any active subscriptions" : "Try changing the filter"}
              />
            </Card>
          ) : (
            <VStack gap="sm">
              {filteredSubscriptions.map((sub) => (
                <Card key={sub.id} padding="md">
                  <HStack gap="md" style={{ alignItems: 'center' }}>
                    <Receipt20Regular style={{ color: 'var(--brand-primary)' }} />
                    <VStack gap="none" style={{ flex: 1 }}>
                      <HStack gap="sm" style={{ alignItems: 'center' }}>
                        <Text weight="medium" style={{ textTransform: 'capitalize' }}>
                          {sub.product_name || sub.plan}
                        </Text>
                        <Badge variant={getStatusVariant(sub.status)} size="sm">
                          {sub.status}
                        </Badge>
                      </HStack>
                      {sub.current_period_end && (
                        <HStack gap="xs" style={{ alignItems: 'center' }}>
                          <Calendar20Regular style={{ color: 'var(--text-muted)', fontSize: '14px' }} />
                          <Text size="sm" color="muted">
                            {sub.status === 'active' ? 'Renews' : 'Ends'}{' '}
                            {new Date(sub.current_period_end).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                    {formatPrice(sub.price, sub.currency, sub.interval) && (
                      <Text weight="medium">
                        {formatPrice(sub.price, sub.currency, sub.interval)}
                      </Text>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleManageSubscription(sub)}
                    >
                      Manage
                    </Button>
                  </HStack>
                </Card>
              ))}
            </VStack>
          )}
        </Animate>
      </VStack>
    </div>
  )
}
